import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import clsx from 'clsx';
import { useEffect } from 'react';
import {
  TimelineAction,
  TimelineAnalyticsTracking,
  TimelineStep,
  TrackingData,
  TimelineConfirmDeliveryAction,
  TimelineCommonAction,
} from '@interfaces/models/timeline';
import { Order } from '@interfaces/models/myOrder';
import { Product } from '@interfaces/models/orderSaleProduct';
import useUser from '@hooks/user/use-user';
import Icon from '@components/atoms/icon/icon';
import { isQcReportV2 } from '@helpers/routing';
import { Popin } from '../../type';
import { chatUrl } from '../../helper';
import styles from '../../product-timeline.module.scss';
import UrlActionView from './url-action';
import PopupActionView from './popup-action';

const ActionView: React.FC<{
  updatePopin: (p: Popin) => void;
  order: Order | null;
  product: Product;
  step: TimelineStep;
  action: TimelineAction;
  analyticsTracking: TimelineAnalyticsTracking;
}> = ({ updatePopin, order, product, step, action, analyticsTracking }) => {
  const { user } = useUser();
  const { sendAnalyticEvent } = useAnalyticEvents('orders_sales_timeline');

  const productId = product.productId;

  const analyticData = { label: order == null ? 'seller' : 'buyer', product_id: productId };

  const link = styles.ordersSales__mainRight__steps__step__box__actions__btnLink;

  const reportLink = styles.ordersSales__mainRight__steps__step__box__actions__reportLink;

  const button = clsx(
    styles.ordersSales__mainRight__steps__step__box__actions__btn,
    'vc-btn vc-btn--full vc-btn--primary',
  );

  const buttonOutline = clsx(
    styles.ordersSales__mainRight__steps__step__box__actions__btn,
    'vc-btn vc-btn--full vc-btn--outline',
  );
  const icon = clsx(styles.ordersSales__mainRight__steps__step__box__actions__btnIcon, 'vc-btn vc-btn--full');
  const { status, relistDetails, trackingStep } = step;

  useEffect(() => {
    if (
      (status === 'ongoing' || status === 'pending') &&
      action.identifier === 'qualityControlReport' &&
      action.type != null &&
      action.type !== 'none'
    ) {
      sendAnalyticEvent('timeline_item_assessment_nego', analyticData);
    }
  }, []); // Have no choice for this warning!

  const onShowShippingDetails = () => {
    sendAnalyticEvent('timeline_edit_carrier_and_options');
    updatePopin({ kind: 'GetShippingLabel', product });
  };

  const onShowUpdateAddressModal = (updateAddressAction: TimelineCommonAction) => {
    const tracking = updateAddressAction.tracking || {};
    sendAnalyticEvent('timeline_update_address', {
      ...analyticData,
      ...tracking,
    });
    updatePopin({ kind: 'UpdateAddress' });
  };

  switch (action.identifier) {
    case 'qualityControlReport':
      switch (action.type) {
        case 'button':
          return (
            <UrlActionView
              url={action.url}
              className={clsx(button, icon)}
              newTab={false}
              onClick={() => {
                const isReportV2 = isQcReportV2(action.url);

                const tracking = action.tracking || ({} as TrackingData);

                sendAnalyticEvent(
                  'timeline_view_report',
                  isReportV2 ? { ...tracking, product_id: product.productId } : analyticData,
                );
              }}
              dataCy={`action-${action.identifier}`}
            >
              <Icon
                className={styles.ordersSales__mainRight__steps__step__box__actions__btnIcon__icon}
                name={'invoice-default'}
                ariaHidden
              />
              {action.label || action.title}
            </UrlActionView>
          );
        case 'link':
          return (
            <UrlActionView
              url={action.url}
              className={clsx(icon, link)}
              newTab={false}
              onClick={() => {
                sendAnalyticEvent('timeline_view_report', analyticData);
              }}
              dataCy={`action-${action.identifier}`}
            >
              <Icon
                className={styles.ordersSales__mainRight__steps__step__box__actions__btnIcon__icon}
                name={'invoice-default'}
                ariaHidden
              />
              {action.title}
            </UrlActionView>
          );
        case 'none':
          return null;
      }
    case 'relist':
      if (relistDetails) {
        const onRelist = () => {
          updatePopin({ kind: 'Relist', order, product, relistDetails: relistDetails });
          sendAnalyticEvent('timeline_relist_item', analyticData);
        };
        return (
          <PopupActionView
            className={button}
            onClick={onRelist}
            dataCy={`action-${action.identifier}`}
          >
            {action.title}
          </PopupActionView>
        );
      } else {
        return <></>;
      }
    case 'returnForm':
      const onReturnForm = () => {
        if (order) {
          updatePopin({
            kind: 'ReturnForm',
            product,
            order,
            returnReasons: action.returnReasons,
          });
          sendAnalyticEvent('timeline_b2c_return', analyticData);
        }
      };
      return (
        <PopupActionView
          className={buttonOutline}
          onClick={onReturnForm}
          dataCy={`action-${action.identifier}`}
        >
          {action.title}
        </PopupActionView>
      );
    case 'confirmDelivery':
      const confirmDeliveryAction = action as TimelineConfirmDeliveryAction;
      if (action.type === 'buyer') {
        const onConfirmDelivery = () => {
          if (action.deliveryType === 'home') {
            sendAnalyticEvent('timeline_home_delivery_confirmation', analyticData);
          } else {
            sendAnalyticEvent('timeline_pickup_confirmation', analyticData);
          }

          updatePopin({
            kind: 'ConfirmDelivery',
            product,
            order,
            confirmDeliveryDetails: confirmDeliveryAction,
          });
        };
        return (
          <PopupActionView
            className={button}
            onClick={onConfirmDelivery}
            dataCy={`action-${action.identifier}`}
          >
            {confirmDeliveryAction.title}
          </PopupActionView>
        );
      } else {
        const onConfirmDelivery = () => {
          const tracking = action.tracking || ({} as TrackingData);
          sendAnalyticEvent('timeline_confirm_delivery', {
            ...analyticData,
            ...tracking,
          });

          updatePopin({
            kind: 'SellerConfirmDelivery',
            product,
            order,
            confirmDeliveryDetails: confirmDeliveryAction,
          });
        };

        return (
          <PopupActionView
            className={button}
            onClick={onConfirmDelivery}
            dataCy={`action-${action.identifier}`}
          >
            {confirmDeliveryAction.title}
          </PopupActionView>
        );
      }
    case 'download':
      return (
        <UrlActionView
          url={action.url}
          className={button}
          newTab={true}
          onClick={() => {
            if (action.type && action.type === 'returnLabel') {
              sendAnalyticEvent('timeline_download_label_return', analyticData);
            } else {
              sendAnalyticEvent('timeline_download_label', analyticData);
            }
          }}
          dataCy={`action-${action.identifier}`}
        >
          {action.title}
        </UrlActionView>
      );
    case 'contact':
      if (action.customerId != null) {
        const tracking = action.tracking || ({} as TrackingData);
        return (
          <UrlActionView
            // Note: from the api, customerId === sellerId, but for create a chat url we need buyer id, the user
            url={chatUrl(action.type === 'buyer' ? user.id : action.customerId, product.productId)}
            className={clsx(icon, 'vc-btn--outline')}
            newTab={true}
            onClick={() => {
              sendAnalyticEvent('timeline_bs_chat', {
                ...tracking,
              });
            }}
            dataCy={`action-${action.identifier}`}
          >
            <Icon
              className={styles.ordersSales__mainRight__steps__step__box__actions__btnIcon__icon}
              name={'chat'}
              ariaHidden
            />
            {action.title}
          </UrlActionView>
        );
      } else {
        return null;
      }
    case 'cancel':
      return (
        <UrlActionView
          url={action.url}
          className={clsx(icon, link)}
          newTab={false}
          onClick={() => {
            sendAnalyticEvent('timeline_cancel_sale', analyticData);
          }}
          dataCy={`action-${action.identifier}`}
        >
          {action.title}
        </UrlActionView>
      );
    case 'report':
      const handleReportIssue = () => {
        if (action.type === 'report') {
          sendAnalyticEvent('timeline_report_issue', {
            ...analyticData,
            property: trackingStep,
            value: analyticsTracking.sellerStatus?.toUpperCase() === 'C2C' ? '0' : '1',
          });
        } else if (action.type === 'viewIssue') {
          sendAnalyticEvent('timeline_view_issue', analyticData);
        }
      };

      return (
        <UrlActionView
          url={action.url}
          className={reportLink}
          newTab={false}
          onClick={() => handleReportIssue()}
          dataCy={`action-${action.identifier}`}
        >
          {action.title}
        </UrlActionView>
      );
    case 'getPaid':
      return (
        <UrlActionView
          url={action.url}
          className={button}
          newTab={false}
          onClick={() => {
            sendAnalyticEvent('timeline_get_paid', {
              ...analyticData,
              ...action.tracking,
            });
          }}
          dataCy={`action-${action.identifier}`}
        >
          <div>{action.title}</div>
        </UrlActionView>
      );
    case 'shippingLabel': {
      return action.type === 'editShippingDetails' ? (
        <PopupActionView
          className={clsx(icon, action.type === 'editShippingDetails' ? 'vc-btn--outline' : 'vc-btn--primary')}
          onClick={onShowShippingDetails}
          dataCy={`action-${action.identifier}`}
        >
          {action.title}
        </PopupActionView>
      ) : null;
    }
    case 'uploadInvoice':
      return (
        <UrlActionView
          url={action.url}
          className={button}
          newTab={false}
          onClick={() => {
            sendAnalyticEvent('timeline_invoice_request_at_authentication', {
              ...analyticData,
              ...action.tracking,
            });
          }}
        >
          <div>{action.title}</div>
        </UrlActionView>
      );
    case 'updateAddress': {
      return (
        <PopupActionView
          className={clsx(icon, 'vc-btn--outline')}
          onClick={() => onShowUpdateAddressModal(action)}
          dataCy={`action-${action.identifier}`}
        >
          {action.title}
        </PopupActionView>
      );
    }
    default:
      return null;
  }
};

export default ActionView;
